import { useCallback, useState } from "react";
import { createPortal } from "react-dom";
import { FeedbackDialog } from "./FeedbackDialog";

/**
 * The small round bug button that opens FeedbackDialog, on every screen.
 *
 * Where it sits is each screen's business, because no one corner is free on all
 * of them: the menu screens have an empty right-hand header slot (ScreenShell
 * puts it there) and the home screen a free top-right corner (`corner`), but on
 * a table the corners belong to the players' decks, so the play screens put it
 * in the HUD strip along the bottom (`hud`, the strip's own chip style). The
 * online table's strip is already full on a narrow phone; there it lives in the
 * rules sheet instead (RulesModal's `onFeedback`).
 *
 * The dialog is portalled to the body: the HUD strip is its own stacking
 * context, and a modal inside it would sit under the table's other overlays.
 */
export function FeedbackButton({ variant = "header" }: { variant?: "header" | "corner" | "hud" }) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const className = variant === "hud" ? "hud-exit feedback-button-hud" : `feedback-button feedback-button-${variant}`;
  return (
    <>
      <button className={className} data-hint="feedback" aria-label="Send feedback" title="Send feedback" onClick={() => setOpen(true)}>
        <BugIcon />
      </button>
      {open && createPortal(<FeedbackDialog onClose={close} />, document.body)}
    </>
  );
}

/** A beetle seen from above, in em so it follows the button's font-size. */
export function BugIcon() {
  return (
    <svg
      className="bug-icon"
      viewBox="0 0 24 24"
      width="1.05em"
      height="1.05em"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      aria-hidden
    >
      <path d="M9 6.5a3 3 0 0 1 6 0" />
      <path d="M8.5 4 9.8 5.6M15.5 4l-1.3 1.6" />
      <rect x="7" y="7.5" width="10" height="12" rx="5" fill="currentColor" fillOpacity="0.18" />
      <path d="M12 9v10" />
      <path d="M7 11H4.5M7 14.5H4M7.6 17.6 5.3 19.8M17 11h2.5M17 14.5h3M16.4 17.6l2.3 2.2" />
    </svg>
  );
}
